import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError, downloadCsv } from "@/lib/api";
import { TRACKS, fmtDate, scoreColor } from "@/lib/constants";
import { TrackBadge } from "@/components/TrackBadge";
import { ScoreRing } from "@/components/ScoreBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Copy, AlertTriangle, MapPin, Calendar, Search, Sparkles, Compass, BadgeCheck, Upload, FileSpreadsheet, Download, ScanSearch, Loader2, Check } from "lucide-react";

export default function TriageInbox() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [track, setTrack] = useState("all");
  const [band, setBand] = useState("all");
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const { data: opps = [], isLoading } = useQuery({ queryKey: ["opps"], queryFn: async () => (await api.get("/opportunities")).data });

  const filtered = opps.filter((o) => {
    if (track !== "all" && o.track !== track) return false;
    const s = o.scores?.total || 0;
    if (band === "high" && s < 80) return false;
    if (band === "mid" && (s < 60 || s >= 80)) return false;
    if (band === "low" && s >= 60) return false;
    if (q && !`${o.title} ${o.org_name}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-5" data-testid="triage-view">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Triage Inbox</h1>
          <p className="text-sm text-muted-foreground mt-1">{filtered.length} of {opps.length} opportunities · every item carries source provenance.</p>
        </div>
        <div className="flex items-center gap-3">
          <SearchGigsDialog onDone={() => qc.invalidateQueries({ queryKey: ["opps"] })} />
          <ImportDialog onDone={() => qc.invalidateQueries({ queryKey: ["opps"] })} />
          <DiscoverDialog onDone={() => qc.invalidateQueries({ queryKey: ["opps"] })} />
          <AddOpportunityDialog open={addOpen} setOpen={setAddOpen} onCreated={() => qc.invalidateQueries({ queryKey: ["opps"] })} />
        </div>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or org…" data-testid="triage-search" className="pl-9" />
        </div>
        <Select value={track} onValueChange={setTrack}>
          <SelectTrigger className="w-[200px]" data-testid="triage-filter-track"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tracks</SelectItem>
            {Object.entries(TRACKS).map(([id, t]) => <SelectItem key={id} value={id}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={band} onValueChange={setBand}>
          <SelectTrigger className="w-[160px]" data-testid="triage-filter-score"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All scores</SelectItem>
            <SelectItem value="high">Strong (80+)</SelectItem>
            <SelectItem value="mid">Moderate (60–79)</SelectItem>
            <SelectItem value="low">Weak (&lt;60)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground py-12 text-center">Loading…</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((o, i) => (
            <Card key={o.id} data-testid={`opportunity-card-${o.id}`}
              onClick={() => navigate(`/opportunity/${o.id}`)}
              className="p-4 border-border hover:bg-accent/50 cursor-pointer transition-colors animate-in-up" style={{ animationDelay: `${Math.min(i * 30, 300)}ms` }}>
              <div className="flex items-center gap-4">
                <ScoreRing score={o.scores?.total || 0} size={56} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold truncate">{o.title}</h3>
                    {o.is_duplicate && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded-full px-2 py-0.5">
                        <AlertTriangle className="h-3 w-3" /> possible duplicate
                      </span>
                    )}
                    {o.ai_suggested && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-sky-400 border border-sky-500/30 bg-sky-500/10 rounded-full px-2 py-0.5" data-testid={`ai-suggested-${o.id}`}>
                        <Sparkles className="h-3 w-3" /> AI-suggested · verify
                      </span>
                    )}
                    {o.status === "promoted" && <span className="text-[11px] text-emerald-400 border border-emerald-500/30 bg-emerald-500/10 rounded-full px-2 py-0.5">promoted</span>}
                    {o.status === "rejected" && <span className="text-[11px] text-rose-400 border border-rose-500/30 bg-rose-500/10 rounded-full px-2 py-0.5">rejected</span>}
                  </div>
                  <div className="text-sm text-muted-foreground mt-0.5">{o.org_name}</div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                    <TrackBadge track={o.track} short />
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {o.location || "—"}</span>
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(o.deadline)}</span>
                    <span className="font-mono">${(o.fee_potential || 0).toLocaleString()}</span>
                    <span className="opacity-70">via {o.source}</span>
                  </div>
                </div>
                <div className="text-right shrink-0 hidden sm:block">
                  <div className={`font-mono font-bold text-lg ${scoreColor(o.scores?.total || 0)}`}>{o.scores?.total || 0}</div>
                  <div className="text-[11px] text-muted-foreground">{o.track_confidence}% conf</div>
                </div>
              </div>
            </Card>
          ))}
          {filtered.length === 0 && <p className="text-sm text-muted-foreground py-12 text-center">No opportunities match these filters.</p>}
        </div>
      )}
    </div>
  );
}

function AddOpportunityDialog({ open, setOpen, onCreated }) {
  const [form, setForm] = useState({ title: "", org_name: "", org_domain: "", event_url: "", source: "manual", source_url: "", description: "", location: "", deadline: "", fee_potential: "", kind: "cfp", contact_email: "", contact_name: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form, fee_potential: form.fee_potential ? Number(form.fee_potential) : null,
        contact: form.contact_email ? { name: form.contact_name, email: form.contact_email } : null };
      delete payload.contact_email; delete payload.contact_name;
      await api.post("/opportunities", payload);
      toast.success("Opportunity added & auto-scored");
      setOpen(false); onCreated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-opportunity-btn" className="gap-2"><Plus className="h-4 w-4" /> Add opportunity</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Add opportunity</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5"><Label>Title *</Label><Input value={form.title} onChange={set("title")} data-testid="add-title" /></div>
          <div className="space-y-1.5"><Label>Organization *</Label><Input value={form.org_name} onChange={set("org_name")} data-testid="add-org" /></div>
          <div className="space-y-1.5"><Label>Domain</Label><Input value={form.org_domain} onChange={set("org_domain")} placeholder="example.org" /></div>
          <div className="space-y-1.5"><Label>Event / CFP URL</Label><Input value={form.event_url} onChange={set("event_url")} /></div>
          <div className="space-y-1.5"><Label>Source</Label><Input value={form.source} onChange={set("source")} /></div>
          <div className="space-y-1.5"><Label>Location</Label><Input value={form.location} onChange={set("location")} /></div>
          <div className="space-y-1.5"><Label>Deadline</Label><Input type="date" value={form.deadline} onChange={set("deadline")} /></div>
          <div className="space-y-1.5"><Label>Fee potential ($)</Label><Input type="number" value={form.fee_potential} onChange={set("fee_potential")} /></div>
          <div className="space-y-1.5"><Label>Kind</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="cfp">CFP</SelectItem><SelectItem value="retreat_lead">Retreat lead</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Contact name</Label><Input value={form.contact_name} onChange={set("contact_name")} /></div>
          <div className="space-y-1.5"><Label>Contact email</Label><Input value={form.contact_email} onChange={set("contact_email")} /></div>
          <div className="col-span-2 space-y-1.5"><Label>Description</Label><Textarea value={form.description} onChange={set("description")} rows={3} data-testid="add-description" /></div>
        </div>
        <DialogFooter>
          <Button disabled={busy || !form.title || !form.org_name} onClick={submit} data-testid="add-submit-btn">{busy ? "Scoring…" : "Add & score"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function DiscoverDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [count, setCount] = useState(5);
  const [focus, setFocus] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setTracks((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/opportunities/discover", { tracks: tracks.length ? tracks : null, count: Number(count), focus: focus || null });
      toast.success(`Discovered ${data.discovered} ${data.ai_generated ? "AI-suggested" : "directory"} opportunit${data.discovered === 1 ? "y" : "ies"} — verify before outreach`);
      setOpen(false); onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="discover-opportunities-btn"><Compass className="h-4 w-4" /> Find opportunities</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Discover opportunities</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Surface candidate public CFPs and retreat leads with your selected AI model. Each result is flagged <span className="text-sky-400">AI-suggested · verify</span> and carries no contact until you confirm it at the public source.</p>
        <div className="space-y-4 pt-1">
          <div>
            <Label className="text-xs text-muted-foreground">Tracks (optional — defaults to all)</Label>
            <div className="space-y-2 mt-2">
              {Object.entries(TRACKS).map(([id, t]) => (
                <label key={id} className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid={`discover-track-${id}`}>
                  <Checkbox checked={tracks.includes(id)} onCheckedChange={() => toggle(id)} />
                  <span className="h-2 w-2 rounded-full" style={{ background: t.dot }} /> {t.label}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>How many</Label>
              <Input type="number" min={1} max={8} value={count} onChange={(e) => setCount(e.target.value)} data-testid="discover-count" />
            </div>
            <div className="space-y-1.5"><Label>Focus (optional)</Label>
              <Input value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="e.g. burnout" data-testid="discover-focus" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={run} disabled={busy} data-testid="discover-run-btn" className="gap-2"><Sparkles className="h-4 w-4" /> {busy ? "Searching…" : "Find opportunities"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ImportDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("childcare_outreach");
  const [track, setTrack] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      if (mode === "auto") fd.append("track", track);
      const { data } = await api.post("/opportunities/import-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      let msg = `Imported ${data.imported} opportunit${data.imported === 1 ? "y" : "ies"}`;
      if (data.queued_for_approval) msg += `, ${data.queued_for_approval} outreach drafts queued`;
      if (data.skipped) msg += `, ${data.skipped} rows skipped`;
      toast.success(msg);
      if (data.personal_inboxes?.length) toast.warning(`${data.personal_inboxes.length} personal inbox(es) flagged — review before sending`);
      setOpen(false); setFile(null); onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setFile(null); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="import-file-btn"><Upload className="h-4 w-4" /> Import CSV</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><FileSpreadsheet className="h-4 w-4 text-primary" /> Import from CSV</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Upload a spreadsheet of contacts or opportunities. Columns like name, organization, email, phone, address are auto-mapped.</p>
        <div className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label>Import as</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="import-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="childcare_outreach">Percussion Pals childcare leads (queue outreach drafts)</SelectItem>
                <SelectItem value="auto">General opportunities (auto-classified)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mode === "auto" && (
            <div className="space-y-1.5">
              <Label>Force track (optional)</Label>
              <Select value={track || "none"} onValueChange={(v) => setTrack(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="import-track-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Auto-detect</SelectItem>
                  {Object.entries(TRACKS).map(([id, t]) => <SelectItem key={id} value={id}>{t.short}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>File (.csv or .xlsx)</Label>
            <label className="flex items-center gap-3 rounded-lg border border-dashed border-border p-4 cursor-pointer hover:bg-accent/40 transition-colors" data-testid="import-dropzone">
              <FileSpreadsheet className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground truncate">{file ? file.name : "Choose a CSV file…"}</span>
              <input type="file" accept=".csv,.txt,.xlsx" className="hidden" data-testid="import-file-input" onChange={(e) => setFile(e.target.files?.[0])} />
            </label>
          </div>
          <button onClick={() => downloadCsv("/import/template.csv", "cadence_import_template.csv")} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="import-template-link">
            <Download className="h-3 w-3" /> Download CSV template
          </button>
        </div>
        <DialogFooter><Button disabled={!file || busy} onClick={submit} data-testid="import-submit-btn">{busy ? "Importing…" : "Import"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SearchGigsDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [count, setCount] = useState(6);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState({});

  const reset = () => { setResults(null); setSelected({}); };

  const runSearch = async () => {
    if (!query.trim()) return;
    setLoading(true); setResults(null);
    try {
      const { data } = await api.post("/opportunities/search", { query, count: Number(count) });
      setResults(data);
      setSelected(Object.fromEntries((data.items || []).map((_, i) => [i, true])));
      if (!data.items?.length) toast.message("No matches — try a broader query");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const addSelected = async () => {
    const items = (results.items || []).filter((_, i) => selected[i]);
    if (!items.length) return;
    setAdding(true);
    try {
      const { data } = await api.post("/opportunities/add-candidates", { items });
      toast.success(`Added ${data.added} gig${data.added === 1 ? "" : "s"} to triage — verify before outreach`);
      setOpen(false); reset(); setQuery(""); onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setAdding(false); }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="search-gigs-btn"><ScanSearch className="h-4 w-4" /> Search gigs</Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ScanSearch className="h-4 w-4 text-primary" /> Search speaking gigs & contracts</DialogTitle></DialogHeader>
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1.5">
            <Label>What are you looking for?</Label>
            <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="e.g. paid keynote on burnout at healthcare conferences 2026" data-testid="search-gigs-input" />
          </div>
          <div className="w-20 space-y-1.5">
            <Label>Count</Label>
            <Input type="number" min={1} max={8} value={count} onChange={(e) => setCount(e.target.value)} data-testid="search-gigs-count" />
          </div>
          <Button onClick={runSearch} disabled={loading || !query.trim()} data-testid="search-gigs-run-btn" className="gap-1.5">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search
          </Button>
        </div>

        {results && (
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">{results.items.length} candidate{results.items.length === 1 ? "" : "s"} · {results.ai_generated ? "AI-sourced" : "directory fallback"} · verify before outreach</p>
              {results.items.length > 0 && <button className="text-xs text-primary hover:underline" onClick={() => setSelected(Object.fromEntries(results.items.map((_, i) => [i, selectedCount !== results.items.length])))}>{selectedCount === results.items.length ? "Deselect all" : "Select all"}</button>}
            </div>
            {results.items.map((it, i) => (
              <label key={i} className="flex gap-3 rounded-lg border border-border p-3 cursor-pointer hover:bg-accent/40 transition-colors" data-testid={`search-result-${i}`}>
                <Checkbox checked={!!selected[i]} onCheckedChange={(v) => setSelected((p) => ({ ...p, [i]: v }))} className="mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{it.title}</div>
                  <div className="text-xs text-muted-foreground">{it.org_name} · {it.location || "—"} · {it.fee_potential ? `$${Number(it.fee_potential).toLocaleString()}` : "fee TBD"}</div>
                  <div className="text-xs text-muted-foreground mt-1">{it.description}</div>
                  <div className="text-[11px] text-sky-400 mt-1">source to verify: {it.source}{it.event_url ? ` · ${it.event_url}` : ""}</div>
                </div>
              </label>
            ))}
          </div>
        )}

        <DialogFooter>
          {results && results.items.length > 0 && (
            <Button onClick={addSelected} disabled={adding || !selectedCount} data-testid="search-add-selected-btn" className="gap-1.5">
              <Check className="h-4 w-4" /> {adding ? "Adding…" : `Add ${selectedCount} to triage`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

